import { createBrowserRouter, Navigate } from 'react-router';
import { Suspense } from 'react';
import { AppLayout } from './layouts/AppLayout';
import { TenantCodeLayout } from './layouts/TenantCodeLayout';
import { lazyWithRetry } from './lib/lazy-with-retry';
import { RouteErrorFallback } from './components/errors/RouteErrorFallback';
import { PageLoader } from './components/ui/feedback/page-loader';
import { ModuleGuard } from './components/auth/ModuleGuard';
import { AdminRoleGuard } from './components/auth/AdminRoleGuard';
import type { TenantModule } from './store/slices/tenantSlice';
import type { RouteObject } from 'react-router';
import { BARE_APPLICATION_SEGMENTS } from './lib/auth/authFlow';

// Lazy loaded pages
const LoginPage = lazyWithRetry(() => import('./pages/LoginPage'));
const UnauthorizedPage = lazyWithRetry(() => import('./pages/UnauthorizedPage'));
const DashboardPage = lazyWithRetry(() => import('./pages/DashboardPage'));
const InboundPage = lazyWithRetry(() => import('./pages/InboundPage'));
const InboundDetailPage = lazyWithRetry(() => import('./pages/InboundDetailPage'));
const OutboundPage = lazyWithRetry(() => import('./pages/OutboundPage'));
const OutboundDetailPage = lazyWithRetry(() => import('./pages/OutboundDetailPage'));
const CreateOutboundPage = lazyWithRetry(() => import('./pages/CreateOutboundPage'));
const CreateInboundPage = lazyWithRetry(() => import('./pages/CreateInboundPage'));
const StockPage = lazyWithRetry(() => import('./pages/StockPage'));
const StockMovementsPage = lazyWithRetry(() => import('./pages/StockMovementsPage'));
const BillingPage = lazyWithRetry(() => import('./pages/BillingPage'));
const UserProfilePage = lazyWithRetry(() => import('./pages/UserProfilePage'));
const RoadTransportPage = lazyWithRetry(() => import('./pages/RoadTransportPage'));
const RoadTransportDetailPage = lazyWithRetry(() => import('./pages/RoadTransportDetailPage'));
const CreateRoadTransportRoutePage = lazyWithRetry(() => import('./pages/CreateRoadTransportRoutePage'));
const AddressBookPage = lazyWithRetry(() => import('./pages/AddressBookPage'));
const CreateRelationPage = lazyWithRetry(() => import('./pages/CreateRelationPage'));
const RelationDetailPage = lazyWithRetry(() => import('./pages/RelationDetailPage'));
const AdminPage = lazyWithRetry(() => import('./pages/AdminPage'));
const AnnouncementsPage = lazyWithRetry(() => import('./pages/AnnouncementsPage'));
const ContainersPage = lazyWithRetry(() => import('./pages/ContainersPage'));
const NotFoundPage = lazyWithRetry(() => import('./pages/NotFoundPage'));

const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
);

const withModuleGuard = (
  Component: React.LazyExoticComponent<React.ComponentType>,
  requiredModule: TenantModule | TenantModule[],
) => (
  <Suspense fallback={<PageLoader />}>
    <ModuleGuard requiredModule={requiredModule}>
      <Component />
    </ModuleGuard>
  </Suspense>
);

// -----------------------------------------------------------------------
// Tenant-prefixed application routes
// -----------------------------------------------------------------------

const sharedRoutes: RouteObject[] = [
  { path: 'dashboard', element: withSuspense(DashboardPage), handle: { title: 'Dashboard' } },
  { path: 'profile', element: withSuspense(UserProfilePage), handle: { title: 'Profile' } },
];

const appRoutes: RouteObject[] = [
  // RoadTransport module
  { path: 'road-transport', element: withModuleGuard(RoadTransportPage, 'RoadTransport'), handle: { title: 'Road Transport' } },
  { path: 'road-transport/new', element: withModuleGuard(CreateRoadTransportRoutePage, 'RoadTransport'), handle: { title: 'New Road Transport' } },
  { path: 'road-transport/draft/:id', element: withModuleGuard(CreateRoadTransportRoutePage, 'RoadTransport'), handle: { title: 'Draft Road Transport' } },
  { path: 'road-transport/:id', element: withModuleGuard(RoadTransportDetailPage, 'RoadTransport'), handle: { title: 'Road Transport Detail' } },
  // Warehouse module
  { path: 'inbound', element: withModuleGuard(InboundPage, 'Warehouse'), handle: { title: 'Inbound' } },
  { path: 'inbound/new', element: withModuleGuard(CreateInboundPage, 'Warehouse'), handle: { title: 'New Inbound' } },
  { path: 'inbound/draft/:id', element: withModuleGuard(CreateInboundPage, 'Warehouse'), handle: { title: 'Draft Inbound' } },
  { path: 'inbound/:id', element: withModuleGuard(InboundDetailPage, 'Warehouse'), handle: { title: 'Inbound Detail' } },
  { path: 'stock', element: withModuleGuard(StockPage, 'Warehouse'), handle: { title: 'Stock' } },
  { path: 'stock/movements', element: withModuleGuard(StockMovementsPage, 'Warehouse'), handle: { title: 'Stock Movements' } },
  { path: 'outbound', element: withModuleGuard(OutboundPage, 'Warehouse'), handle: { title: 'Outbound' } },
  // Specific outbound paths must come before `outbound/:id` so "new" / "draft" are not captured as ids.
  { path: 'outbound/new', element: withModuleGuard(CreateOutboundPage, 'DraftOutboundOrders'), handle: { title: 'New Outbound' } },
  { path: 'outbound/draft/:id', element: withModuleGuard(CreateOutboundPage, 'DraftOutboundOrders'), handle: { title: 'Draft Outbound' } },
  { path: 'outbound/:id', element: withModuleGuard(OutboundDetailPage, 'Warehouse'), handle: { title: 'Outbound Detail' } },
  // Billing module
  { path: 'billing', element: withModuleGuard(BillingPage, 'Billing'), handle: { title: 'Billing' } },
  // Containers module
  { path: 'containers', element: withModuleGuard(ContainersPage, 'Containers'), handle: { title: 'Containers' } },
  // DraftOutboundOrders / DraftRoadTransportOrders modules — either unlocks Organizations
  { path: 'address-book/new', element: withModuleGuard(CreateRelationPage, ['DraftOutboundOrders', 'DraftRoadTransportOrders']), handle: { title: 'New Organization' } },
  { path: 'address-book/:id', element: withModuleGuard(RelationDetailPage, ['DraftOutboundOrders', 'DraftRoadTransportOrders']), handle: { title: 'Organization Detail' } },
  { path: 'address-book', element: withModuleGuard(AddressBookPage, ['DraftOutboundOrders', 'DraftRoadTransportOrders']), handle: { title: 'Organizations' } },
  {
    path: 'admin',
    element: (
      <AdminRoleGuard>
        {withSuspense(AdminPage)}
      </AdminRoleGuard>
    ),
    handle: { title: 'Admin' },
  },
  { path: 'announcements', element: withSuspense(AnnouncementsPage), handle: { title: 'Announcements' } },
];

const legacyBareRoutes: RouteObject[] = [...BARE_APPLICATION_SEGMENTS].map(
  (segment) => ({
    path: `/${segment}/*`,
    element: <PageLoader />,
    handle: { title: 'Redirecting' },
  }),
);

const routes: RouteObject[] = [
  { path: '/auth/callback', element: <PageLoader />, errorElement: <RouteErrorFallback />, handle: { title: 'Signing in' } },
  { path: '/login', element: withSuspense(LoginPage), errorElement: <RouteErrorFallback />, handle: { title: 'Login' } },
  { path: '/unauthorized', element: withSuspense(UnauthorizedPage), errorElement: <RouteErrorFallback />, handle: { title: 'Unauthorized' } },
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouteErrorFallback />,
    children: [{ index: true, element: <PageLoader /> }],
  },
  ...legacyBareRoutes,
  {
    path: '/:tenantCode',
    element: <TenantCodeLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      { index: true, element: <Navigate to="dashboard" replace /> },
      ...sharedRoutes,
      ...appRoutes,
      { path: '*', element: withSuspense(NotFoundPage), handle: { title: 'Not Found' } },
    ],
  },
  { path: '*', element: withSuspense(NotFoundPage), handle: { title: 'Not Found' } },
];

export const router = createBrowserRouter(routes);
